/**
 * Analytics sending, URL-change tracking, and search-page event helpers.
 *
 * Moved from templates/components/scripts/analytics-sender.html (Phase 3 refactor).
 * Handlebars dependencies (onlineServices URL, analyticsRoute, enableSearchSpring) are
 * read at call-time from window.BC_CONTEXT which is populated by init-globals.html.
 *
 * Event listener registration (window.load, pageshow, onUrlChange) has moved to
 * global.js onReady() via initAnalyticsSenderListeners().
 */

import { hasValidExpectedKey, loyaltyProgramField } from '../../common/utils/cookie-utils';
import createLogger from '../../common/utils/logger';

const log = createLogger('AnalyticsSender');

export async function sendOnlineServiceAnalyticsEvent(onlineServiceEvent) {
    if (typeof onlineServiceEvent === "undefined") {
        log.error('Error sending sendOnlineServiceAnalytics: onlineServiceEvent is undefined');
        return null;
    }
    let osOrderClient = window.BC_CONTEXT?.onlineServices || '';
    let route = window.BC_CONTEXT?.analyticsRoute || '';
    if (window.BC_CONTEXT?.site_environment === 'local') {
        osOrderClient = 'http://localhost:5110/';
        route = '';
    }

    // Read beacon events configuration
    const beaconEventsList = 'swatch_click,name_click,image_click,star_review_click,add_to_cart_start,remove_from_cart_start,subCategory_click,express_checkout_start';
    const beaconEvents = beaconEventsList
        ? beaconEventsList.split(',').map(e => e.trim()).filter(e => e)
        : [];
    const shouldUseBeacon = beaconEvents.includes(onlineServiceEvent?.Name);

    const url = `${osOrderClient}${route}tags/tag`;
    const raw = JSON.stringify(onlineServiceEvent);

    // Strategy: Use sendBeacon for configured events, fallback to fetch otherwise
    if (shouldUseBeacon) {
        log.debug(`[Analytics] Using sendBeacon for event: ${onlineServiceEvent?.Name}`);

        if (navigator.sendBeacon) {
            try {
                const payload = new Blob([raw], { type: 'application/json' });
                const sent = navigator.sendBeacon(url, payload);

                if (sent) {
                    log.info(`[Analytics] Event "${onlineServiceEvent?.Name}" queued via sendBeacon`);
                    return { success: true, method: 'sendBeacon' };
                } else {
                    log.warn(`[Analytics] sendBeacon returned false for event: ${onlineServiceEvent?.Name}, falling back to fetch`);
                }
            } catch (beaconError) {
                log.warn('[Analytics] sendBeacon failed:', beaconError, 'falling back to fetch');
            }
        } else {
            log.warn('[Analytics] sendBeacon not supported, falling back to fetch');
        }
    }

    // Use fetch for non-beacon events OR as fallback
    try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const requestOptions = {
            method: "Post",
            headers: myHeaders,
            body: raw,
            redirect: "follow",
            // Survive page unload (link-click navigations) instead of being aborted.
            // Skip for large bodies: keepalive shares a ~64KB in-flight quota and throws past it.
            keepalive: raw.length < 60000,
        };

        const response = await fetch(url, requestOptions);

        if (!response.ok) {
            let message = `HTTP error! status: ${response.status} sending sendOnlineServiceAnalytics`;
            if (typeof onlineServiceEvent !== "undefined") {
                message += ` for event: ${onlineServiceEvent?.Name}`;
            }
            log.error('Error sending sendOnlineServiceAnalytics:', message, response);
            throw new Error(message);
        }

        const contentType = response.headers.get("content-type");
        let data;

        try {
            if (contentType && contentType.includes("application/json")) {
                data = await response.json();
            } else {
                data = await response.text();
            }
            return data;

        } catch (jsonError) {
            let message = `Error parsing JSON response sending sendOnlineServiceAnalytics`;
            log.error('Error parsing JSON response sending sendOnlineServiceAnalytics:', jsonError, response);
            if (typeof onlineServiceEvent !== "undefined") {
                message += ` for event: ${onlineServiceEvent?.Name}`;
            }
            message += `: ${jsonError.message}`;
            throw new Error(message);
        }

    } catch (error) {
        let message = `Network error sending sendOnlineServiceAnalytics: ${error?.message}`;
        if (typeof onlineServiceEvent !== "undefined") {
            message += ` for event: ${onlineServiceEvent?.Name}`;
        }
        log.error(message, error);
        throw new Error(message);
    }
}

/**
 * Build and send a canonical "pageAction" interaction event through the shared
 * online-services sender. Used for fire-and-forget UI interactions (e.g.
 * remove_from_cart_start) that were previously sent via the removed AnalyticsQueue.
 *
 * Mirrors the event shape produced by buildOnlineServiceAnalyticsEventFile()
 * (capital Name/EventType + identity merge), minus the async ClientIpAddress
 * lookup so the beacon fires without waiting on a network round-trip.
 *
 * @param {string} name - canonical event name (must be in the beaconEvents list to use sendBeacon)
 * @param {object} metaData - event-specific payload
 * @returns {Promise} resolves when sendOnlineServiceAnalyticsEvent settles
 */
export function sendInteractionEvent(name, metaData = {}) {
    const safeParse = (raw) => { try { return JSON.parse(raw) || {}; } catch { return {}; } };
    const local = safeParse(localStorage.getItem('onlineServicesHealth')).onlineServicesHealthLocal || {};
    const session = safeParse(sessionStorage.getItem('onlineServicesHealth')).onlineServicesHealthSession || {};

    const event = {
        ...local,                                   // DeviceId, VisitorId, VisitorIds
        ...session,                                 // SessionId
        Host: window.location.host,
        FullURL: window.location.href,
        Name: name,                                 // canonical capital-N (matches beaconEvents check)
        EventType: 'pageAction',                    // hardcoded; mapEventToType() doesn't list this event
        ApplicationId: window.BC_CONTEXT?.appId,
        ServedIpAddress: window.BC_CONTEXT?.client_ip_address,
        // This path skips cleanMetaData, so the payload ships verbatim — the spreadable
        // helper is what keeps `loyalty_program: ''` off the wire for guests.
        metaData: { ...metaData, ...loyaltyProgramField() },
    };

    return sendOnlineServiceAnalyticsEvent(event);
}

export function saveVisitQueryParams(key = "firstVisitParams", expirationDays = 30, domain, paramsWhitelist = null) {
    const expectedKeys = [
        "adgroupid", "content", "targetid", "utm_term", "utm_campaign", "utm_source",
        "utm_medium", "campaignid", "gad_source", "gclid", "msclkid", "GBRAID",
        "WBRAID", "fbclid", "ttclid", "ttadid", "ttcid", "ttsid", "ttclid_exp"
    ];

    let valuedQueryParamsArray = [];

    const queryParamsLocalStorage = localStorage.getItem("queryParamsArrayLocalStorage");
    let queryParamsLocalStorageParsed;
    if (queryParamsLocalStorage != null) {
        queryParamsLocalStorageParsed = JSON.parse(queryParamsLocalStorage);
    }

    const latestQueryStringParameters = localStorage.getItem("latestQueryStringParameters");
    let latestQueryStringParametersParsed;
    if (latestQueryStringParameters != null) {
        latestQueryStringParametersParsed = JSON.parse(latestQueryStringParameters);
    }

    const navEntry = performance.getEntriesByType('navigation')[0];
    const queryParamsCurrent = typeof window.parseQueryStringToObjectWithDefaultKeys === 'function'
        ? window.parseQueryStringToObjectWithDefaultKeys(navEntry?.name)
        : {};
    const currentPageHasValuedParam = hasValidExpectedKey(queryParamsCurrent, expectedKeys);

    if (navEntry && navEntry.redirectCount > 0 && window.navigation?.activation?.from?.url) {
        const queryParamsRedirectPage = typeof window.parseQueryStringToObjectWithDefaultKeys === 'function'
            ? window.parseQueryStringToObjectWithDefaultKeys(window.navigation.activation.from.url)
            : {};
        const RedirectPageHasValuedParam = hasValidExpectedKey(queryParamsRedirectPage, expectedKeys);
    }

    let newVal;
    if (currentPageHasValuedParam === true) {
        if (latestQueryStringParametersParsed != null) {
            newVal = typeof window.compareMetrics === 'function'
                ? window.compareMetrics(latestQueryStringParametersParsed, queryParamsCurrent)
                : null;
            if (newVal) {
                if (queryParamsLocalStorageParsed != null) {
                    queryParamsLocalStorageParsed.push(queryParamsCurrent);
                } else {
                    queryParamsLocalStorageParsed = [queryParamsCurrent];
                }
            }
        } else {
            queryParamsLocalStorageParsed = [queryParamsCurrent];
        }
        window.setWithExpiry?.("latestQueryStringParameters", queryParamsCurrent, 7776000000, 'local');
        localStorage.setItem("queryParamsArrayLocalStorage", JSON.stringify(queryParamsLocalStorageParsed));
    }

    log.debug("newVal: ", newVal);

    const params = new URLSearchParams(window.location.search);
    const paramObj = {};

    for (const [k, v] of params.entries()) {
        if (!paramsWhitelist || paramsWhitelist.includes(k)) {
            paramObj[k] = v;
        }
    }

    log.debug("paramObj: ", paramObj);
}

export function onSearchPageContentsChange() {
    const pageType = window.getPageType?.() || window.BC_CONTEXT?.pageType || '';
    let newSiteErrorEvent = {
        loadNumber: 0,
        scriptLoaded: false,
        interactionsCompleted: false,
        errorMessage: {
            eventName: "page_view",
            eventMessage: "",
            pageType,
            timeStamp: new Date().toISOString()
        }
    };
    // getPageType() transforms BC's 'category' → 'product_listing' or 'search_results';
    // use BC_CONTEXT.pageType (raw BC value) for the condition so both paths match.
    const rawPageType = window.BC_CONTEXT?.pageType || '';
    if (rawPageType === 'category' || rawPageType === 'search') {
        log.debug("onSearchPageContentsChange");
        const eventData = typeof window.initializeUtagDataWithCustomer === 'function'
            ? window.initializeUtagDataWithCustomer()
            : {};
        const queryParams = typeof window.parseQueryString === 'function' ? window.parseQueryString() : {};
        eventData.search_keyword = String(queryParams.searchQuery);
        eventData.search_facet = String(queryParams.facetSelect);
        eventData.search_keyword_fallback = String(queryParams.searchQueryFallback);
        eventData.search_page = queryParams.pageNumber;
        eventData.tealium_event = queryParams.searchQuery == null ? 'product_listing' : 'search results';
        const ssTotal = typeof window.GetSearchResults === 'function' ? window.GetSearchResults() : 0;
        log.debug("onUrlChange GetSearchResults total:", ssTotal);
        eventData.search_results = ssTotal > 0 ? String(ssTotal) : "-1";
        eventData.page_name = location.pathname + location.search;
        eventData.navigation_type = 'internal';
        // user_id — always include to maintain user continuity across SPA navigations
        const userId = localStorage.getItem('uniqueId');
        if (userId != null) {
            eventData.user_id = userId;
        }
        // product_listing fields — mirror processCategoryPageUtagData else-branch
        if (queryParams.searchQuery == null) {
            const breadcrumbCats = typeof window.breadcrumbToCategories === 'function'
                ? window.breadcrumbToCategories()
                : { categoryCurrent: undefined, subCategoryCurrent: undefined };
            const Cat = breadcrumbCats.categoryCurrent;
            const subCat = breadcrumbCats.subCategoryCurrent;
            const cartGrandTotal = window.LG_PAGE_DATA?.cartGrandTotal || '';
            const customer = window.LG_PAGE_DATA?.customer;
            const categoryProductArray = (window.LG_PAGE_DATA?.category?.products || []).map(p => p.sku).filter(Boolean);
            const cartItems = window.LG_PAGE_DATA?.cartItems || [];
            const categoryArray = cartItems.flatMap(i =>
                Array.isArray(i.category_names) ? i.category_names : (i.category_names ? [i.category_names] : [])
            ).filter(Boolean);
            const rootCategories = [];
            const subCategories = [];
            categoryArray.forEach(category => {
                const parts = category.split('/');
                const rootCat = parts[0];
                const subCatPart = parts.slice(1).join('/');
                if (!rootCategories.includes(rootCat)) rootCategories.push(rootCat);
                if (subCatPart && !subCategories.includes(subCatPart)) subCategories.push(subCatPart);
            });
            eventData.cart_total = cartGrandTotal;
            eventData.cart_product_category = rootCategories;
            eventData.cart_product_subcategory = subCategories;
            eventData.cart_product_category_id = [];
            eventData.cart_product_subcategory_id = [];
            eventData.page_category = Cat;
            eventData.page_subcategory = subCat;
            eventData.ip_address = window.BC_CONTEXT?.client_ip_address || '';
            eventData.product_category = [Cat];
            eventData.product_subcategory = [subCat];
            eventData.total = cartGrandTotal;
            eventData.customer_phone = customer?.phone || '';
            eventData.customer_city = customer?.shipping_address?.city || '';
            eventData.customer_id = customer?.id ? String(customer.id) : '';
            eventData.customer_state = customer?.shipping_address?.state || '';
            eventData.customer_zip = customer?.shipping_address?.zip || '';
            eventData.customer_country = customer?.shipping_address?.country_id || '';
            eventData.checkout_step_name = '';
            eventData.checkout_step_number = '';
            //eventData.products_displayed = categoryProductArray;
        }
        const topCat = typeof window.getMostRepresentedCategory === 'function' ? window.getMostRepresentedCategory() : '';
        if (topCat) {
            eventData.most_represented_category = topCat;
        }
        window.queueEventFile?.(newSiteErrorEvent, eventData);
    }
}

export function onUrlChange(callback) {
    const originalPushState = history.pushState;
    history.pushState = function (...args) {
        originalPushState.apply(this, args);
        callback(window.location.href);
    };

    const originalReplaceState = history.replaceState;
    history.replaceState = function (...args) {
        originalReplaceState.apply(this, args);
        callback(window.location.href);
    };

    window.addEventListener('popstate', () => {
        callback(window.location.href);
    });

    window.addEventListener('hashchange', () => {
        callback(window.location.href);
    });
}

export function getPageProductIds() {
    const arrayProductIds = [];
    const formActionButtons = document.querySelectorAll("#ss__content--search > div > ul > li > article > div.card-section-rightWrapper > div.card-section-rightWrapper--inner > div.card-section--buttons > a");

    formActionButtons.forEach(button => {
        arrayProductIds.push({
            productId: button.dataset.productId
        });
    });
    const recommenderProductIds = document.querySelectorAll("article > div.card-section-lefttWrapper > figure > figcaption > div > a");
    if (recommenderProductIds) {
        recommenderProductIds.forEach(recButton => {
            arrayProductIds.push({
                productId: recButton.dataset.productId
            });
        });
    }

    return arrayProductIds;
}

/**
 * Register window-level event listeners that were previously inline in the HBS partial.
 * Must be called from global.js onReady() (fires on DOMContentLoaded, before window.load).
 *
 * @param {Function} pageLoadErrorEventsFn - imported from event-loop.js
 */
export function initAnalyticsSenderListeners(pageLoadErrorEventsFn) {
    // window.load fires after DOMContentLoaded, so registering here is safe
    window.addEventListener('load', pageLoadErrorEventsFn, false);

    // Handle bfcache (Back-Forward Cache) restores
    window.addEventListener('pageshow', function(event) {
        if (!event.persisted) return;
        const pageType = window.getPageType?.() || window.BC_CONTEXT?.pageType || '';
        const bfcacheEvent = {
            loadNumber: 0,
            scriptLoaded: false,
            interactionsCompleted: false,
            errorMessage: {
                eventName: 'page_view',
                eventMessage: 'bfcache_restore',
                pageType,
                timeStamp: new Date().toISOString()
            }
        };
        const bfcacheData = { ...window.basePageViewEvent, navigation_type: 'back_forward_cache' };
        window.queueEventFile?.(bfcacheEvent, bfcacheData);
    });

    // Handle URL changes (SearchSpring SPA navigation)
    const customSearchProviderEnabled = window.BC_CONTEXT?.enableSearchSpring;
    onUrlChange(() => {
        if (customSearchProviderEnabled === 'true' && window.initialPageViewSent) {
            setTimeout(() => onSearchPageContentsChange(), 1000);
        }
    });
}
